import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const MODULES = [
  { value: 'AP_INVOICE', label: 'AP Invoice' },
  { value: 'CASH_RECON', label: 'Cash-Recon' },
  { value: 'GL_SYNC', label: 'GL Sync' },
  { value: 'WORKFORCE', label: 'Workforce' },
  { value: 'CHARGEBACK', label: 'Chargeback' },
];

const TRIGGERS = [
  { value: 'SUBMIT', label: 'Submit' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'REJECT', label: 'Reject' },
  { value: 'RETURN', label: 'Return' },
  { value: 'COMPLETE', label: 'Complete' },
];

const defaultForm = {
  module: 'AP_INVOICE',
  triggerEvent: 'SUBMIT',
  workflowStepName: '',
  recipientType: 'ASSIGNED_USER',
  notificationEmail: '',
  subject: 'Invoice Approval Required - {{invoiceNumber}}',
  messageTemplate:
    'Dear {{assignedUserName}},\n\nInvoice {{invoiceNumber}} from {{vendorName}} for {{amount}} has been submitted and requires your approval.\n\nPlease log in to Conductor to review the invoice.',
  deliveryMethod: 'BOTH',
  isActive: true,
};

const NotificationConfiguration = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.NOTIFICATION_CONFIGURATION);
  const tenantId = getTenantId();
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchRules = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiFetch(`conductor/notification-rules/tenant/${tenantId}`);
      setRows((Array.isArray(data) ? data : []).map((item) => ({
        id: item.notificationRuleId,
        ...item,
      })));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load notification rules');
    } finally {
      setLoading(false);
    }
  }, [tenantId, showError]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const columns = useMemo(() => [
    { field: 'module', headerName: 'Module', width: 140 },
    { field: 'triggerEvent', headerName: 'Trigger', width: 120 },
    { field: 'workflowStepName', headerName: 'Workflow Step', width: 160 },
    { field: 'recipientType', headerName: 'Recipient', width: 160 },
    { field: 'notificationEmail', headerName: 'Email ID', width: 200 },
    { field: 'deliveryMethod', headerName: 'Delivery', width: 110 },
    { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (params.value ? 'Yes' : 'No'),
    },
  ], []);

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setForm({
      module: selectedRow.module || 'AP_INVOICE',
      triggerEvent: selectedRow.triggerEvent || 'SUBMIT',
      workflowStepName: selectedRow.workflowStepName || '',
      recipientType: selectedRow.recipientType || 'ASSIGNED_USER',
      notificationEmail: selectedRow.notificationEmail || '',
      subject: selectedRow.subject || '',
      messageTemplate: selectedRow.messageTemplate || '',
      deliveryMethod: selectedRow.deliveryMethod || 'BOTH',
      isActive: selectedRow.isActive !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      const userId = getUserId();
      const payload = {
        tenantId,
        ...form,
        isActive: Boolean(form.isActive),
        updatedBy: userId,
      };
      if (mode === 'add') {
        await apiFetch('conductor/notification-rules', {
          method: 'POST',
          body: JSON.stringify({ ...payload, createdBy: userId }),
        });
        showSuccess('Notification rule saved');
      } else {
        await apiFetch(`conductor/notification-rules/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showSuccess('Notification rule updated');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchRules();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/notification-rules/${selectedRow.id}`, { method: 'DELETE' });
      showSuccess('Notification rule deleted');
      setDeleteOpen(false);
      setSelectionModel([]);
      fetchRules();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Notification Configuration</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit rule' : 'No edit permission'}>
            <span>
              <IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canAdd ? 'Add rule' : 'No add permission'}>
            <span>
              <IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete rule' : 'No delete permission'}>
            <span>
              <IconButton
                onClick={() => setDeleteOpen(true)}
                disabled={!selectedRow || !canDelete}
                sx={harmonatePlainIconButtonSx}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Paper sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          disableMultipleRowSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          sx={{ ...harmonateDataGridSx, flex: 1, minHeight: 0 }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>
          {mode === 'add' ? 'Add Notification Rule' : 'Edit Notification Rule'}
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth size="small" label="Module" value={form.module} onChange={(e) => setForm((p) => ({ ...p, module: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              {MODULES.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
            <TextField select fullWidth size="small" label="Trigger Event" value={form.triggerEvent} onChange={(e) => setForm((p) => ({ ...p, triggerEvent: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              {TRIGGERS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
            </TextField>
          </Box>
          <TextField fullWidth size="small" label="Workflow Step (optional)" value={form.workflowStepName} onChange={(e) => setForm((p) => ({ ...p, workflowStepName: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select fullWidth size="small" label="Recipient" value={form.recipientType} onChange={(e) => setForm((p) => ({ ...p, recipientType: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ASSIGNED_USER">Assigned user</MenuItem>
              <MenuItem value="CONFIGURED_EMAIL">Configured email</MenuItem>
              <MenuItem value="BOTH">Assigned user and email</MenuItem>
            </TextField>
            <TextField select fullWidth size="small" label="Delivery Method" value={form.deliveryMethod} onChange={(e) => setForm((p) => ({ ...p, deliveryMethod: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="IN_APP">In-app</MenuItem>
              <MenuItem value="EMAIL">Email</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </TextField>
          </Box>
          <TextField fullWidth size="small" type="email" label="Notification Email ID" value={form.notificationEmail} onChange={(e) => setForm((p) => ({ ...p, notificationEmail: e.target.value }))} />
          <TextField fullWidth size="small" label="Subject" value={form.subject} onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} />
          <TextField fullWidth size="small" multiline minRows={5} label="Message Template" value={form.messageTemplate} onChange={(e) => setForm((p) => ({ ...p, messageTemplate: e.target.value }))} helperText="Variables: {{invoiceNumber}} {{vendorName}} {{amount}} {{assignedUserName}} {{transactionStatus}}" />
          <TextField select fullWidth size="small" label="Status" value={form.isActive ? 'ACTIVE' : 'INACTIVE'} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.value === 'ACTIVE' }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleSave} disabled={submitting} variant="contained" sx={harmonateProceedButtonSx}>{submitting ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>Delete this notification rule?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} disabled={deleteLoading} variant="contained" sx={harmonateAbortButtonSx}>{deleteLoading ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default NotificationConfiguration;
